"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { EventDay } from "./types";
import { SectionLink, UI, useText } from "./shared";

export function EventDayNavigation({ days }: { days: EventDay[] }) {
  const { text } = useText();
  const [active, setActive] = useState(days[0]?.id ?? "");
  const [top, setTop] = useState(56);
  const navRef = useRef<HTMLElement | null>(null);

  // Sit directly under the site header, whatever height it renders at.
  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;
    const main = navRef.current?.closest("main");
    const update = () => {
      const headerHeight = Math.round(header.getBoundingClientRect().height);
      setTop(headerHeight);
      main?.style.setProperty(
        "--event-scroll-offset",
        `${headerHeight + (navRef.current?.offsetHeight ?? 80) + 20}px`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    if (navRef.current) observer.observe(navRef.current);
    return () => observer.disconnect();
  }, []);

  // Highlight the day currently occupying the upper part of the viewport.
  useEffect(() => {
    const sections = days
      .map((day) => document.getElementById(day.id))
      .filter((node): node is HTMLElement => Boolean(node));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-35% 0px -55% 0px" },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [days]);

  // Keep the active tab visible inside the horizontally scrolling nav on mobile.
  useEffect(() => {
    const nav = navRef.current;
    const tab = nav?.querySelector<HTMLElement>(`[data-day="${active}"]`);
    if (!nav || !tab) return;
    const offset = tab.offsetLeft - (nav.clientWidth - tab.clientWidth) / 2;
    nav.scrollTo({
      left: Math.max(0, offset),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [active]);

  if (days.length === 0) return null;
  return (
    <nav
      ref={navRef}
      aria-label={text(UI.eventDays)}
      style={{ top }}
      className="event-dark sticky z-40 overflow-x-auto border-y border-white/10 bg-[#071b4a]/95 backdrop-blur-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <div className="mx-auto flex w-max min-w-full max-w-[1280px] gap-2 px-4 py-2.5 sm:justify-center sm:gap-3 sm:px-6 sm:py-3">
        {days.map((day) => {
          const isActive = day.id === active;
          return (
            <SectionLink
              key={day.id}
              to={day.id}
              data-day={day.id}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63] sm:px-6 sm:text-base",
                isActive
                  ? "border-[#f6cf63] bg-[#f6cf63] text-[#041127] shadow-[0_8px_24px_rgba(246,207,99,0.25)]"
                  : "border-white/15 text-white/85 hover:border-[#f6cf63]/60 hover:text-white",
              )}
            >
              <span>
                {text(UI.day)} {day.number}
              </span>
              <span
                aria-hidden="true"
                className={isActive ? "text-[#041127]/50" : "text-white/35"}
              >
                ·
              </span>
              <span
                className={
                  isActive ? "font-medium" : "font-normal text-white/70"
                }
              >
                {text(day.shortDate)}
              </span>
            </SectionLink>
          );
        })}
      </div>
    </nav>
  );
}
